// Co-located with OAuthAcademySignUpStep/ while there's only one consumer.
// When a second OAuth client adopts this hook, lift to
// `app/javascript/unauthenticated/shared/hooks/`. The hook is named after
// the `practitionerOauthSignUp` mutation it wraps and is intentionally generic
// across OAuth clients — Academy is just the first consumer.
import { useState, type Dispatch, type SetStateAction } from "react";

import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { usePractitionerOauthSignUpUnauthenticatedSharedMutation } from "@unauthenticated/shared/data/mutations/PractitionerOauthSignUp.mutation.generated";

interface PractitionerOauthSignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  tosTimestamp: Date | "";
}

// Canonical existing-account error string. Set by
// `Auth::Practitioner::SignUp` (apps/authentication/services/auth/practitioner/sign_up.rb)
// and surfaced via `errors.fields.email`. If the backend changes the wording,
// the hook silently falls back to the generic-error branch — the spec asserts on
// this exact string so a backend change would break the spec.
const EXISTING_ACCOUNT_ERROR = "An account with this email exists";

interface Args {
  clientId: string;
  onSignUpComplete: () => void;
}

interface Return {
  submit: (formData: PractitionerOauthSignUpFormData) => void;
  isLoading: boolean;
  submitError: string | null;
  passwordError: string | null;
  setPasswordError: (value: string | null) => void;
  isAccountExists: boolean;
  setIsAccountExists: Dispatch<SetStateAction<boolean>>;
  recaptchaError: boolean;
}

const usePractitionerOauthSignUpSubmission = ({ clientId, onSignUpComplete }: Args): Return => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isAccountExists, setIsAccountExists] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);

  const [runMutation, { loading: mutationLoading }] =
    usePractitionerOauthSignUpUnauthenticatedSharedMutation();

  const submit = (formData: PractitionerOauthSignUpFormData) => {
    setSubmitError(null);
    setPasswordError(null);
    setIsAccountExists(false);
    setRecaptchaError(false);

    gRecaptchaExecute((captchaToken: string) => {
      void runMutation({
        variables: {
          input: {
            attributes: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              password: formData.password,
              tosAcceptedAt:
                formData.tosTimestamp instanceof Date ? formData.tosTimestamp.toISOString() : "",
              clientId,
            },
            captchaToken,
          },
        },
        onCompleted: data => {
          const payload = data?.auth?.practitionerOauthSignUp;

          if (payload?.practitioner) {
            setHasNavigated(true);
            onSignUpComplete();

            return;
          }

          const emailErrors = payload?.errors?.fields?.email ?? [];
          if (emailErrors.includes(EXISTING_ACCOUNT_ERROR)) {
            setIsAccountExists(true);

            return;
          }

          const passwordErrors = payload?.errors?.fields?.password ?? [];
          if (passwordErrors.length > 0) {
            setPasswordError(passwordErrors[0]);

            return;
          }

          setSubmitError(payload?.errors?.message ?? "Something went wrong. Please try again.");
        },
        onError: ({ message }) => {
          setSubmitError(message);
        },
      });
    }, setRecaptchaError);
  };

  return {
    submit,
    // Stays true after a successful response so the form's submit button
    // does not flip back to enabled while OAuthAuthorizationStep mounts and
    // POSTs to `/api/oauth/authorize`.
    isLoading: mutationLoading || hasNavigated,
    submitError,
    passwordError,
    setPasswordError,
    isAccountExists,
    setIsAccountExists,
    recaptchaError,
  };
};

export { usePractitionerOauthSignUpSubmission };
